<script setup>
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

defineProps({
  greeting: { type: String, required: true },
  subtitle: { type: String, default: '' },
  continueLabel: { type: String, default: 'Continue' },
  skipLabel: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

defineEmits(['continue', 'skip']);
</script>

<template>
  <div
    class="relative flex min-h-screen w-full items-start justify-center overflow-hidden bg-n-surface-2 px-4 py-8 text-body-main sm:px-8 sm:py-12"
  >
    <div
      class="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgb(var(--blue-4)),transparent_38%),radial-gradient(circle_at_bottom_right,rgb(var(--teal-3)),transparent_42%)] opacity-70"
    />

    <div class="relative w-full max-w-[68rem]">
      <header
        class="mb-6 flex items-center justify-between rounded-2xl border border-n-weak bg-n-solid-1/90 px-5 py-4 shadow-sm backdrop-blur sm:px-7"
      >
        <div class="flex items-center gap-3">
          <img
            :src="'/botz/favicon_64x64.png'"
            alt="BOTZ CRM"
            class="size-10 rounded-xl"
          />
          <div>
            <p class="text-base font-semibold text-n-slate-12">BOTZ CRM</p>
            <p class="text-xs text-n-slate-10">
              {{ t('ONBOARDING_NEXT.DASHBOARD_LABEL') }}
            </p>
          </div>
        </div>
        <div
          class="hidden items-center gap-2 rounded-full bg-n-teal-3 px-3 py-1.5 text-xs font-medium text-n-teal-11 sm:flex"
        >
          <span class="size-2 rounded-full bg-n-teal-9" />
          {{ t('ONBOARDING_NEXT.WORKSPACE_STATUS') }}
        </div>
      </header>

      <main
        class="overflow-hidden rounded-3xl border border-n-weak bg-n-solid-1/95 shadow-xl backdrop-blur"
      >
        <div class="border-b border-n-weak px-6 py-8 sm:px-10 sm:py-10">
          <div class="flex items-start gap-4">
            <div
              class="flex size-11 flex-shrink-0 items-center justify-center rounded-2xl bg-n-blue-3 text-n-blue-10"
            >
              <slot name="greeting-icon">
                <Icon icon="i-lucide-sparkles" class="size-5" />
              </slot>
            </div>
            <div>
              <h1
                class="text-2xl font-semibold tracking-tight text-n-slate-12 sm:text-3xl"
              >
                {{ greeting }}
              </h1>
              <p
                v-if="subtitle"
                class="mt-1 text-sm text-n-slate-11 sm:text-base"
              >
                {{ subtitle }}
              </p>
            </div>
          </div>
        </div>

        <div class="grid gap-5 p-6 sm:p-10 lg:grid-cols-2">
          <slot />
        </div>

        <footer
          class="flex flex-col-reverse gap-3 border-t border-n-weak bg-n-surface-2/60 px-6 py-5 sm:flex-row sm:items-center sm:justify-end sm:px-10"
        >
          <NextButton
            v-if="skipLabel"
            type="button"
            slate
            outline
            class="justify-center sm:min-w-44"
            @click="$emit('skip')"
          >
            {{ skipLabel }}
          </NextButton>
          <NextButton
            type="submit"
            blue
            :is-loading="isLoading"
            :disabled="disabled"
            class="justify-center sm:min-w-44"
            @click="$emit('continue')"
          >
            {{ continueLabel }}
          </NextButton>
        </footer>
      </main>
    </div>
  </div>
</template>
